/* @jsx h */
import { defineComponent, h, ref, watch, PropType } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { normalizeStaffType, type Staff, type StaffType } from "@lemmonhq/types";

export type StaffFormState = {
  name: string;
  email: string;
  password: string;
  pin: string;
  staff_type: string;
  role_name: string;
  print_receipt: number;
};

const STAFF_TYPES: { value: StaffType; labelKey: string }[] = [
  { value: "Front of house", labelKey: "settings.staff.type_front_of_house" },
  { value: "Kitchen", labelKey: "settings.staff.type_kitchen" },
  { value: "Bar", labelKey: "settings.staff.type_bar" },
  { value: "Delivery", labelKey: "settings.staff.type_delivery" },
];

const defaultForm = (): StaffFormState => ({
  name: "",
  email: "",
  password: "",
  pin: "",
  staff_type: "Front of house",
  role_name: "staff",
  print_receipt: 1,
});

const staffToFormState = (staff: Staff): StaffFormState => ({
  name: staff.name,
  email: staff.email ?? "",
  password: "",
  pin: "",
  // Legacy rows store "kitchen" or null; show the matching option (or none).
  staff_type: normalizeStaffType(staff.staff_type) ?? "",
  role_name: staff.role === "admin" ? "manager" : staff.role,
  print_receipt: staff.print_receipt,
});

export const StaffForm = defineComponent({
  name: "StaffForm",
  props: {
    mode: {
      type: String as PropType<"create" | "edit">,
      required: true,
    },
    staff: {
      type: Object as PropType<Staff | null>,
      default: null,
    },
    onSubmit: {
      type: Function as PropType<(data: StaffFormState) => Promise<void>>,
      required: true,
    },
    onDelete: {
      type: Function as PropType<() => Promise<void>>,
      default: null,
    },
    onCancel: {
      type: Function as PropType<() => void>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useLocalization();
    const submitting = ref(false);

    const form = ref<StaffFormState>(
      props.mode === "edit" && props.staff
        ? staffToFormState(props.staff)
        : defaultForm(),
    );

    watch(
      () => [props.mode, props.staff] as const,
      ([mode, staff]) => {
        form.value =
          mode === "edit" && staff ? staffToFormState(staff) : defaultForm();
      },
      { immediate: true },
    );

    const handleSubmit = async (event: Event) => {
      event.preventDefault();
      if (submitting.value) return;
      try {
        submitting.value = true;
        await props.onSubmit(form.value);
      } finally {
        submitting.value = false;
      }
    };

    const handleDelete = async () => {
      if (!props.onDelete || submitting.value) return;
      try {
        submitting.value = true;
        await props.onDelete();
      } finally {
        submitting.value = false;
      }
    };

    const sectionLabel =
      "text-xs font-semibold uppercase tracking-[0.18em] text-accent";

    return () => (
      <form class="flex h-full flex-col" onSubmit={handleSubmit}>
        <div class="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          {/* Name */}
          <label class="form-control w-full">
            <span class="label-text font-medium">
              {t("settings.staff.full_name_label")}
              <span class="ml-0.5 text-error">*</span>
            </span>
            <input
              type="text"
              value={form.value.name}
              onInput={(e) =>
                (form.value.name = (e.target as HTMLInputElement).value)
              }
              placeholder={t("settings.staff.full_name_placeholder")}
              class="input input-bordered w-full"
              required
            />
          </label>

          {/* Role */}
          <div>
            <div class={`${sectionLabel} mb-3`}>
              {t("settings.staff.role_label")}
            </div>
            <div class="join w-full">
              <button
                type="button"
                class={`join-item btn flex-1 ${form.value.role_name === "staff" ? "btn-primary" : "btn-outline"}`}
                onClick={() => (form.value.role_name = "staff")}
              >
                {t("settings.staff.role_staff")}
              </button>
              <button
                type="button"
                class={`join-item btn flex-1 ${form.value.role_name === "manager" ? "btn-primary" : "btn-outline"}`}
                onClick={() => (form.value.role_name = "manager")}
              >
                {t("settings.staff.role_manager")}
              </button>
            </div>
          </div>

          {/* Staff type */}
          <div>
            <div class={`${sectionLabel} mb-3`}>
              {t("settings.staff.staff_type_label")}
            </div>
            <div class="join w-full">
              {STAFF_TYPES.map(({ value, labelKey }) => (
                <button
                  type="button"
                  key={value}
                  class={`join-item btn flex-1 text-xs ${form.value.staff_type === value ? "btn-primary" : "btn-outline"}`}
                  onClick={() => (form.value.staff_type = value)}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>
          </div>

          {/* Login credentials — optional */}
          <div class="space-y-4 rounded-xl border  bg-base-200/40 p-4">
            <div>
              <div class={`${sectionLabel} mb-0.5`}>
                {t("settings.staff.login_section_label")}
              </div>
              <p class="text-xs text-base-content/55">
                {t("settings.staff.login_section_hint")}
              </p>
            </div>

            <div class="grid gap-3 md:grid-cols-2">
              <label class="form-control w-full">
                <span class="label-text text-sm">
                  {t("settings.staff.email_label")}
                </span>
                <input
                  type="email"
                  value={form.value.email}
                  onInput={(e) =>
                    (form.value.email = (e.target as HTMLInputElement).value)
                  }
                  placeholder={t("settings.staff.email_placeholder")}
                  class="input input-bordered input-sm w-full"
                />
              </label>

              <label class="form-control w-full">
                <span class="label-text text-sm">
                  {t("settings.staff.password_label")}
                  {props.mode === "edit" ? (
                    <span class="ml-1 text-xs font-normal text-base-content/50">
                      {t("settings.staff.password_keep_current")}
                    </span>
                  ) : null}
                </span>
                <input
                  type="password"
                  value={form.value.password}
                  onInput={(e) =>
                    (form.value.password = (e.target as HTMLInputElement).value)
                  }
                  placeholder="••••••••"
                  class="input input-bordered input-sm w-full"
                />
              </label>

              <label class="form-control w-full">
                <span class="label-text text-sm">
                  {t("settings.staff.pin_label")}
                </span>
                <input
                  type="text"
                  value={form.value.pin}
                  onInput={(e) =>
                    (form.value.pin = (e.target as HTMLInputElement).value)
                  }
                  placeholder={t("settings.staff.pin_placeholder")}
                  class="input input-bordered input-sm w-full"
                  maxlength={10}
                  pattern="[0-9]*"
                  inputmode="numeric"
                />
              </label>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap items-center justify-between gap-3 border-t bg-base-200/50 px-6 py-4">
          <div class="flex-1">
            {props.mode === "edit" ? (
              <button
                type="button"
                class="btn btn-ghost btn-sm text-error"
                onClick={handleDelete}
                disabled={submitting.value}
              >
                {t("settings.staff.delete_button")}
              </button>
            ) : null}
          </div>

          <div class="flex gap-2">
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              onClick={() => props.onCancel()}
              disabled={submitting.value}
            >
              {t("settings.staff.cancel_button")}
            </button>
            <button
              type="submit"
              class="btn btn-primary btn-sm"
              disabled={submitting.value}
            >
              {submitting.value ? (
                <>
                  <span class="loading loading-spinner loading-sm" />
                  {props.mode === "create"
                    ? t("settings.staff.creating_button")
                    : t("settings.staff.saving_button")}
                </>
              ) : props.mode === "create" ? (
                t("settings.staff.create_button")
              ) : (
                t("settings.staff.save_button")
              )}
            </button>
          </div>
        </div>
      </form>
    );
  },
});
